import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, Check, Trash2, ChevronDown, ExternalLink, Calendar, Car, Wrench, Undo2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Reusable admin inbox — used for messages / test-drives / trade-ins.
 * Config-driven so the same UI serves all three types.
 *
 * Delete uses inline confirmation (NOT window.confirm, which is blocked
 * inside sandboxed preview iframes).
 */
const CONFIG = {
  messages: {
    testid: "page-admin-messages",
    endpoint: "/admin/messages",
    title: "Contact Messages",
    subtitle: "Everything sent through the Contact page.",
    icon: Mail,
    empty: "No messages yet. Your inbox is at zero.",
    primaryField: (r) => r.subject || "General inquiry",
    secondaryField: (r) => r.message,
  },
  "test-drives": {
    testid: "page-admin-test-drives",
    endpoint: "/admin/test-drives",
    title: "Test Drive Requests",
    subtitle: "People who want to come take a car for a spin.",
    icon: Car,
    empty: "No test-drive requests yet.",
    primaryField: (r) => r.vehicle_label || "Any vehicle",
    secondaryField: (r) => r.message || `Preferred date: ${r.preferred_date || "—"}`,
  },
  "trade-ins": {
    testid: "page-admin-trade-ins",
    endpoint: "/admin/trade-ins",
    title: "Trade-In Submissions",
    subtitle: "Vehicles offered for trade-in valuation.",
    icon: Wrench,
    empty: "No trade-in submissions yet.",
    primaryField: (r) => `${r.year} ${r.make} ${r.model}`,
    secondaryField: (r) => r.notes || `${r.mileage?.toLocaleString?.() || r.mileage} mi · Condition: ${r.condition}`,
  },
};

export default function AdminInbox({ kind }) {
  const cfg = CONFIG[kind];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(cfg.endpoint)
      .then((r) => setRows(r.data))
      .catch(() => toast.error(`Failed to load ${cfg.title.toLowerCase()}`))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); setConfirmDeleteId(null); /* eslint-disable-next-line */ }, [kind]);

  const toggleHandled = async (row) => {
    try {
      await api.patch(`${cfg.endpoint}/${row.id}`, { handled: !row.handled });
      toast.success(row.handled ? "Reopened" : "Marked as handled");
      load();
    } catch { toast.error("Update failed"); }
  };

  const softDelete = async (row) => {
    try {
      await api.delete(`${cfg.endpoint}/${row.id}`);
      toast.success("Moved to Trash", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/admin/trash/${kind}/${row.id}/restore`);
              toast.success("Restored");
              load();
            } catch { toast.error("Restore failed"); }
          },
        },
      });
      setConfirmDeleteId(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch { return iso; }
  };

  const Icon = cfg.icon;

  return (
    <div data-testid={cfg.testid}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-display text-5xl md:text-6xl tracking-tighter">{cfg.title}</h1>
        <p className="text-white/50 mt-3 text-sm">{cfg.subtitle}</p>
      </motion.div>

      <div className="mt-10 space-y-3">
        {loading ? (
          <div className="glass rounded-2xl p-12 text-center text-white/40">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center">
            <Icon size={22} className="mx-auto text-white/30" />
            <div className="text-white/60 mt-4">{cfg.empty}</div>
          </div>
        ) : rows.map((row) => {
          const open = expandedId === row.id;
          const confirming = confirmDeleteId === row.id;
          return (
            <div
              key={row.id}
              data-testid={`inbox-row-${row.id}`}
              className={`glass rounded-2xl overflow-hidden transition-colors ${row.handled ? "opacity-60" : ""}`}
            >
              <button
                type="button"
                onClick={() => setExpandedId(open ? null : row.id)}
                className="w-full text-left px-6 py-5 flex items-start gap-4"
              >
                <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${row.handled ? "bg-white/20" : "bg-[var(--primary)]"}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="font-display text-2xl tracking-tight">{row.full_name}</div>
                    <div className="text-xs text-white/40">{formatDate(row.created_at)}</div>
                    {row.handled && (
                      <span className="text-[10px] uppercase tracking-widest text-white/40">Handled</span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-white/70">{cfg.primaryField(row)}</div>
                  <div className="mt-1 text-xs text-white/40 truncate">{cfg.secondaryField(row)}</div>
                </div>
                <ChevronDown size={16} className={`text-white/40 transition-transform mt-2 ${open ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {open && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden border-t border-white/10"
                  >
                    <div className="px-6 py-6 space-y-4">
                      <ContactBlock row={row} />
                      {kind === "messages" && (
                        <Section label="Message">
                          <p className="text-white/80 leading-relaxed whitespace-pre-wrap">{row.message}</p>
                        </Section>
                      )}
                      {kind === "test-drives" && (
                        <>
                          <Section label="Vehicle"><p className="text-white/80">{row.vehicle_label || "—"}</p></Section>
                          <Section label="Preferred date">
                            <p className="text-white/80 inline-flex items-center gap-2">
                              <Calendar size={14} /> {row.preferred_date || "—"}
                            </p>
                          </Section>
                          {row.message && (
                            <Section label="Note"><p className="text-white/80 whitespace-pre-wrap">{row.message}</p></Section>
                          )}
                        </>
                      )}
                      {kind === "trade-ins" && (
                        <>
                          <Section label="Their vehicle">
                            <p className="text-white/80">{row.year} {row.make} {row.model}</p>
                          </Section>
                          <Section label="Mileage & condition">
                            <p className="text-white/80">{row.mileage?.toLocaleString?.()} mi · {row.condition}</p>
                          </Section>
                          {row.notes && (
                            <Section label="Notes"><p className="text-white/80 whitespace-pre-wrap">{row.notes}</p></Section>
                          )}
                        </>
                      )}

                      <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10 items-center">
                        <a href={`mailto:${row.email}`} className="btn-outline text-xs" data-testid={`inbox-reply-${row.id}`}>
                          <Mail size={12} /> Reply by email
                        </a>
                        {row.phone && (
                          <a href={`tel:${row.phone.replace(/[^\d+]/g, "")}`} className="btn-outline text-xs">
                            <Phone size={12} /> Call
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleHandled(row)}
                          data-testid={`inbox-handled-${row.id}`}
                          className={row.handled ? "btn-outline text-xs" : "btn-primary text-xs"}
                        >
                          <Check size={12} /> {row.handled ? "Mark as new" : "Mark as handled"}
                        </button>

                        <div className="ml-auto flex items-center gap-2">
                          {confirming ? (
                            <>
                              <span className="text-xs text-white/50">Move to trash?</span>
                              <button
                                type="button"
                                onClick={() => softDelete(row)}
                                data-testid={`inbox-confirm-delete-${row.id}`}
                                className="text-xs px-3 py-1.5 rounded-full bg-[var(--primary)] text-white hover:brightness-110"
                              >
                                Yes, delete
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteId(null)}
                                data-testid={`inbox-cancel-delete-${row.id}`}
                                className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-white/60 hover:text-white"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteId(row.id)}
                              data-testid={`inbox-delete-${row.id}`}
                              className="btn-outline text-xs text-white/60 hover:text-red-400 hover:border-red-500/40"
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Section = ({ label, children }) => (
  <div>
    <div className="overline mb-2">{label}</div>
    {children}
  </div>
);

const ContactBlock = ({ row }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
    <div>
      <div className="overline mb-1">Email</div>
      <a href={`mailto:${row.email}`} className="text-white/80 hover:text-[var(--primary)] inline-flex items-center gap-1">
        {row.email} <ExternalLink size={11} />
      </a>
    </div>
    {row.phone && (
      <div>
        <div className="overline mb-1">Phone</div>
        <a href={`tel:${row.phone.replace(/[^\d+]/g, "")}`} className="text-white/80 hover:text-[var(--primary)]">
          {row.phone}
        </a>
      </div>
    )}
    <div>
      <div className="overline mb-1">Received</div>
      <div className="text-white/60">{new Date(row.created_at).toLocaleString()}</div>
    </div>
  </div>
);
